import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const Navigation: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // ユーザーのモーション設定を確認
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // スクロール検知
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = useCallback(() => {
    i18n.changeLanguage(i18n.language === 'en' ? 'ja' : 'en');
  }, [i18n]);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // メニューアイテムを最適化
  const menuItems = useMemo(() => ({
    // PC用の全メニュー
    full: [
      { key: 'home', href: '#home' },
      { key: 'about', href: '#about' },
      { key: 'entertainment', href: '#entertainment' },
      { key: 'menu', href: '#menu' },
      { key: 'reservation', href: '#reservation' },
      { key: 'access', href: '#access' },
      { key: 'iris', href: '/iris', isRoute: true },
    ],
    // モバイル用の全メニュー
    mobile: [
      { key: 'home', href: '#home' },
      { key: 'about', href: '#about' },
      { key: 'entertainment', href: '#entertainment' },
      { key: 'menu', href: '#menu' },
      { key: 'reservation', href: '#reservation' },
      { key: 'access', href: '#access' },
      { key: 'iris', href: '/iris', isRoute: true },
    ]
  }), []);

  type MenuKey = 'home' | 'menu' | 'about' | 'entertainment' | 'reservation' | 'access';
  type NavKey = `nav.${MenuKey}`;

  // ESCキーでモバイルメニューを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMobileMenu();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // スクロール防止
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen, closeMobileMenu]);

  return (
    <>
      <nav 
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-black/98 backdrop-blur-md border-b border-[#ff8c00]/30 shadow-lg' 
            : 'bg-black/95 backdrop-blur-sm border-b border-[#ff8c00]/20'
        }`}
        role="navigation"
        aria-label="メインナビゲーション"
      >
        <div className="container mx-auto px-2 md:px-4">
          <div className="flex justify-between items-center h-14 md:h-20">
            {/* Logo */}
            <motion.a
              href="/"
              className="text-lg md:text-2xl font-serif font-bold text-white flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#ff8c00] rounded"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
              aria-label="Fastiny Entertainment Bar ホーム"
            >
              Fastiny
            </motion.a>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-6">
              {menuItems.full.map((item) => (
                item.isRoute ? (
                  <Link
                    key={item.key}
                    to={item.href}
                    className="text-white hover:text-[#ff8c00] transition-colors text-base font-medium px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-[#ff8c00]"
                    aria-label={`${t(`nav.${item.key}` as NavKey)}ページへ移動`}
                  >
                    {t(`nav.${item.key}` as NavKey)}
                  </Link>
                ) : (
                <motion.a
                    key={item.key}
                    href={item.href}
                    className="text-white hover:text-[#ff8c00] transition-colors text-base font-medium px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-[#ff8c00]"
                  whileHover={prefersReducedMotion ? {} : { y: -2 }}
                  whileTap={prefersReducedMotion ? {} : { y: 0 }}
                    aria-label={`${t(`nav.${item.key}` as NavKey)}セクションへ移動`}
                >
                    {t(`nav.${item.key}` as NavKey)}
                </motion.a>
                )
              ))}
              
              {/* 言語切替ボタン - Desktop */}
              <motion.button
                onClick={toggleLanguage}
                className="px-4 py-2 border border-[#ff8c00] rounded-lg text-[#ff8c00] hover:bg-[#ff8c00] hover:text-white transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#ff8c00]"
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                aria-label={`言語を${i18n.language === 'en' ? '日本語' : '英語'}に切り替え`}
              >
                {i18n.language === 'en' ? 'JP' : 'EN'}
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center space-x-2">
              {/* 言語切替ボタン - Mobile */}
              <motion.button
                onClick={toggleLanguage}
                className="px-2 py-1 border border-[#ff8c00] rounded text-[#ff8c00] hover:bg-[#ff8c00] hover:text-white transition-colors text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#ff8c00]"
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                aria-label={`言語を${i18n.language === 'en' ? '日本語' : '英語'}に切り替え`}
              >
                {i18n.language === 'en' ? 'JP' : 'EN'}
              </motion.button>

              {/* ハンバーガーメニューボタン */}
              <button
                onClick={toggleMobileMenu}
                className="p-2 text-white hover:text-[#ff8c00] transition-colors focus:outline-none focus:ring-2 focus:ring-[#ff8c00] rounded"
                aria-label={isMobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <svg 
                  className="w-6 h-6" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* モバイルメニューオーバーレイ */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* 背景オーバーレイ */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-40 md:hidden"
              onClick={closeMobileMenu}
              aria-hidden="true"
            />
            
            {/* メニューパネル */}
            <motion.div
              id="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ 
                type: prefersReducedMotion ? 'tween' : 'spring', 
                damping: 20, 
                stiffness: 100 
              }}
              className="fixed top-0 right-0 h-full w-80 bg-black/95 backdrop-blur-lg border-l border-[#ff8c00]/30 z-50 md:hidden overflow-y-auto"
              role="dialog"
              aria-label="モバイルナビゲーションメニュー"
            >
              <div className="p-6">
                {/* メニューヘッダー */}
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-xl font-bold text-white">メニュー</h2>
                  <button
                    onClick={closeMobileMenu}
                    className="p-2 text-white hover:text-[#ff8c00] transition-colors focus:outline-none focus:ring-2 focus:ring-[#ff8c00] rounded"
                    aria-label="メニューを閉じる"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* メニューアイテム */}
                <nav className="space-y-4" role="navigation" aria-label="モバイルメニュー">
                  {menuItems.mobile.map((item, index) => (
                    <motion.div
                      key={item.key}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {item.isRoute ? (
                        <Link
                          to={item.href}
                          className="block text-white hover:text-[#ff8c00] transition-colors text-lg font-medium py-3 px-4 rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#ff8c00]"
                          onClick={closeMobileMenu}
                          aria-label={`${t(`nav.${item.key}` as NavKey)}ページへ移動`}
                        >
                          {t(`nav.${item.key}` as NavKey)}
                        </Link>
                      ) : (
                        <a
                          href={item.href}
                          className="block text-white hover:text-[#ff8c00] transition-colors text-lg font-medium py-3 px-4 rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#ff8c00]"
                          onClick={closeMobileMenu}
                          aria-label={`${t(`nav.${item.key}` as NavKey)}セクションへ移動`}
                        >
                          {t(`nav.${item.key}` as NavKey)}
                        </a>
                      )}
                    </motion.div>
                  ))}
                </nav>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation; 